import { UserCog, ArrowLeftRight, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

/**
 * Sticky banner rendered at the very top of the admin console while a
 * superadmin is impersonating another account. Provides a one-click
 * "return to my session" affordance. Uses high-contrast amber to make
 * it impossible to forget you're in a borrowed session.
 *
 * Placement is inside `AdminLayout` above the top navigation so it
 * appears on every admin route without cluttering individual pages.
 */
export default function ImpersonationBanner() {
  const { isImpersonating, impersonatingBy, admin, stopImpersonating } = useAuth();
  const [busy, setBusy] = useState(false);

  if (!isImpersonating) return null;

  const handleStop = async () => {
    setBusy(true);
    try {
      await stopImpersonating();
      toast.success('Volviste a tu cuenta de superadmin.');
    } catch (e) {
      toast.error('No se pudo restaurar la sesión original. Vuelve a iniciar sesión.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="w-full bg-amber-500 text-white shadow-md border-b-2 border-amber-700"
      data-testid="impersonation-banner"
      role="alert"
    >
      <div className="max-w-full px-4 py-2 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <UserCog size={18} className="shrink-0" />
          <span className="text-sm font-medium truncate">
            Estás viendo la app como&nbsp;
            <strong className="font-semibold">{admin?.name || admin?.email}</strong>
            &nbsp;({admin?.role})
            {impersonatingBy?.email && (
              <> · sesión original: <span className="opacity-90">{impersonatingBy.email}</span></>
            )}
          </span>
        </div>
        <button
          onClick={handleStop}
          disabled={busy}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-white text-amber-800 text-sm font-semibold hover:bg-amber-50 disabled:opacity-60 shrink-0"
          data-testid="impersonation-stop-btn"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <ArrowLeftRight size={14} />}
          Volver a mi cuenta
        </button>
      </div>
    </div>
  );
}
